/* eslint-disable tsdoc/syntax */
import React, { FunctionComponent, ReactNode, useState } from 'react';
import {
  AssessmentScreen,
  CoverageScreenName,
  GAD7ScreenName,
  MedicareAccessScreenName,
  PapScreenName,
  ScreenType,
  SharedScreenName,
  Us65PlusScreenName,
} from '../../config/FlowTypes';
import { MENO_CONFIG, MenoScreenName } from '../../config/meno/MenoConfig';
import { COVERAGE_RESULTS_LAYOUT, COVERAGE_SCREENS } from '../../config/nerva/CoverageConfig';
import {
  MEDICARE_ACCESS_SCREENS,
  MEDICARE_ACCESS_STATE_LAYOUT,
} from '../../config/nerva/MedicareAccessConfig';
import { NERVA_CONTROL_CONFIG, NervaControlScreenName } from '../../config/nerva/NervaControl';
import { NERVA_HCP_REFERRED_PERSONALISE_CONFIG } from '../../config/nerva/NervaHCPReferredPersonalise';
import { QUIT_CONFIG, QuitScreenName } from '../../config/quit/QuitConfig';
import { useAppStore } from '../../store/Store';
import { appVariant } from '../../util/AppUtils';
import * as images from '../assets/Images';
import { AiLoadingScreen, AiLoadingScreenConfig } from '../flows/AiLoadingScreen';
import { AuthScreen, AuthScreenConfig } from '../flows/AuthScreen';
import { ClassifyScreen, ClassifyScreenConfig } from '../flows/ClassifyScreen';
import { CONFIGURABLE_SCREENS } from '../flows/ConfigurableScreens';
import { DateOfBirthScreen } from '../flows/DateOfBirthScreen';
import { DownloadAppScreen, DownloadAppScreenConfig } from '../flows/DownloadAppScreen';
import { DropdownSelectScreen } from '../flows/DropdownSelectScreen';
import { ExpectationSettingScreen } from '../flows/ExpectationSettingScreen';
import { ExternalLinkScreen, ExternalLinkScreenConfig } from '../flows/ExternalLinkScreen';
import { FinishAccountScreen, FinishAccountScreenConfig } from '../flows/FinishAccountScreen';
import { useFlowNavigator } from '../flows/FlowNavigation';
import { HcpReferredScreen, HcpReferredScreenConfig } from '../flows/HcpReferredScreen';
import {
  HypnotisabilityResultsScreen,
  HypnotisabilityResultsScreenConfig,
} from '../flows/HypnotisabilityResultsScreen';
import {
  InsuranceProviderScreen,
  InsuranceProviderScreenConfig,
} from '../flows/InsuranceProviderScreen';
import { LoadingResultsScreen, LoadingResultsScreenConfig } from '../flows/LoadingResultsScreen';
import {
  MoneyBackGuaranteeScreen,
  MoneyBackGuaranteeScreenConfig,
} from '../flows/MoneyBackGuaranteeScreen';
import { MultiSelectScreen, MultiSelectScreenConfig } from '../flows/MultiSelectScreen';
import {
  NumberedSingleSelectScreen,
  NumberedSingleSelectScreenConfig,
} from '../flows/NumberedSingleSelectScreen';
import { PaymentScreen, PaymentScreenConfig } from '../flows/PaymentScreen';
import { PhoneNumberScreen, PhoneNumberScreenConfig } from '../flows/PhoneNumberScreen';
import { PresentationScreen, PresentationScreenConfig } from '../flows/PresentationScreen';
import { ProgramBenefitsScreen, ProgramBenefitsScreenConfig } from '../flows/ProgramBenefitsScreen';
import {
  ProgramExperienceScreen,
  ProgramExperienceScreenConfig,
} from '../flows/ProgramExperienceScreen';
import { ProgramOverviewScreen, ProgramOverviewScreenConfig } from '../flows/ProgramOverviewScreen';
import {
  ProgramStructureScreen,
  ProgramStructureScreenConfig,
} from '../flows/ProgramStructureScreen';
import { ReferralScreen, ReferralScreenConfig } from '../flows/ReferralScreen';
import { ResultsScreen, ResultsScreenConfig } from '../flows/ResultsScreen';
import { ScreenshotScreen, ScreenshotScreenConfig } from '../flows/ScreenshotScreen';
import { ShareLinkScreen, ShareLinkScreenConfig } from '../flows/ShareLinkScreen';
import { SingleInputScreen, SingleInputScreenConfig } from '../flows/SingleInputScreen';
import { SingleSelectScreen, SingleSelectScreenConfig } from '../flows/SingleSelectScreen';
import { SocialProofScreen, SocialProofScreenConfig } from '../flows/SocialProofScreen';
import {
  TrialExplanationScreen,
  TrialExplanationScreenConfig,
} from '../flows/TrialExplanationScreen';
import { UserInfoRecapScreen, UserInfoRecapScreenConfig } from '../flows/UserInfoRecapScreen';
import {
  CoverageAddressScreen,
  CoverageAddressScreenConfig,
} from '../flows/coverage/CoverageAddressScreen';
import {
  CoverageEmployerScreen,
  CoverageEmployerScreenConfig,
} from '../flows/coverage/CoverageEmployerScreen';
import {
  CoverageResultsScreen,
  CoverageResultsScreenConfig,
} from '../flows/coverage/CoverageResultsScreen';
import { MedicareAccessCoverageLoadingScreen } from '../flows/medicareAccess/MedicareAccessCoverageLoadingScreen';
import { MedicareAccessHelplinesScreen } from '../flows/medicareAccess/MedicareAccessHelplines';
import { MedicareAccessMbiScreen } from '../flows/medicareAccess/MedicareAccessMbiScreen';
import { MedicareAccessSafetyCheckScreen } from '../flows/medicareAccess/MedicareAccessSafetyCheckScreen';
import { PapDiscountResultScreen } from '../flows/patientAssistance/PapDiscountResultScreen';
import { PapHouseholdIncomeScreen } from '../flows/patientAssistance/PapHouseholdIncomeScreen';
import { PapPublicAssistanceScreen } from '../flows/patientAssistance/PapPublicAssistanceScreen';
import { Us65PlusGrantAccessScreen } from '../flows/us65Plus/Us65PlusGrantAccessScreen';
import { WorksBestScreen, WorksBestScreenConfig } from '../trial/WorksBestScreen';
import { DemoScreensComponent, DemoScreensHeader, DemoScreensWrapper } from './DemoScreens';

type ConfigurableScreenTypes = keyof typeof CONFIGURABLE_SCREENS;

type ScreenToSideLinkTable = Record<
  ConfigurableScreenTypes,
  { screenName: AssessmentScreen; title: string }
>;
// Side link name and id mapping used to navigate to a specific demo screen
export const screenToSideLinkTable: ScreenToSideLinkTable = {
  [ScreenType.Auth]: { screenName: SharedScreenName.Auth, title: 'Auth' },
  [ScreenType.ExternalLink]: {
    screenName: NervaControlScreenName.HCPCallToAction,
    title: 'External Link',
  },
  [ScreenType.Classify]: {
    screenName: SharedScreenName.Classify,
    title: 'Classify',
  },
  [ScreenType.DownloadApp]: {
    screenName: SharedScreenName.DownloadApp,
    title: 'Download App',
  },
  [ScreenType.FinishAccount]: {
    screenName: SharedScreenName.FinishAccount,
    title: 'Finish Account',
  },
  [ScreenType.LoadingResults]: {
    screenName: SharedScreenName.LoadingResults,
    title: 'Loading Results',
  },
  [ScreenType.MoneyBackGuarantee]: {
    screenName: NervaControlScreenName.MoneyBackGuarantee,
    title: 'Money Back Guarantee',
  },
  [ScreenType.MultiSelect]: {
    screenName: SharedScreenName.HealthIssues,
    title: 'Multi Select',
  },
  [ScreenType.Payment]: {
    screenName: SharedScreenName.Payment,
    title: 'Payment',
  },
  [ScreenType.PhoneNumber]: {
    screenName: SharedScreenName.PhoneNumber,
    title: 'Phone Number',
  },
  [ScreenType.Presentation]: {
    screenName: SharedScreenName.MaintenanceExperience,
    title: 'Presentation',
  },
  [ScreenType.ProgramBenefits]: {
    screenName: SharedScreenName.ProgramBenefits,
    title: 'Program Benefits',
  },
  [ScreenType.ProgramExperience]: {
    screenName: SharedScreenName.MaintenanceExperience,
    title: 'Program Experience',
  },
  [ScreenType.ProgramStructure]: {
    screenName: SharedScreenName.ProgramStructure,
    title: 'Program Structure',
  },
  [ScreenType.HypnotisabilityResults]: {
    screenName: SharedScreenName.HypnotisabilityResults,
    title: 'Hypnotisability Results',
  },
  [ScreenType.Referral]: {
    screenName: SharedScreenName.Referral,
    title: 'Referral',
  },
  [ScreenType.Results]: {
    screenName: SharedScreenName.Results,
    title: 'Results',
  },
  [ScreenType.Screenshot]: {
    screenName: SharedScreenName.CoreProgramExperience,
    title: 'Screenshot',
  },
  [ScreenType.ShareLink]: {
    screenName: SharedScreenName.ShareLink,
    title: 'Share Link',
  },
  [ScreenType.SingleInput]: {
    screenName: SharedScreenName.Age,
    title: 'Single Input',
  },
  [ScreenType.SingleSelect]: {
    screenName: SharedScreenName.Gender,
    title: 'Single Select',
  },
  [ScreenType.NumberedSingleSelect]: {
    screenName: GAD7ScreenName.Gad7Nervous,
    title: 'Numbered Single Select',
  },
  [ScreenType.SocialProof]: {
    screenName: MenoScreenName.EviaSocialProof,
    title: 'Social Proof',
  },
  [ScreenType.TrialExplanation]: {
    screenName: SharedScreenName.TrialExplanation,
    title: 'Trial Explanation',
  },
  [ScreenType.UserInfoRecap]: {
    screenName: SharedScreenName.UserInfoRecap,
    title: 'User Info Recap',
  },
  [ScreenType.WorksBest]: {
    screenName: SharedScreenName.WorksBest,
    title: 'Works Best',
  },
  [ScreenType.DateOfBirth]: {
    screenName: SharedScreenName.DateOfBirth,
    title: 'Date of Birth',
  },
  [ScreenType.HcpReferred]: {
    screenName: NervaControlScreenName.WelcomeHcpReferred,
    title: 'Welcome HCP Referred',
  },
  [ScreenType.AiLoading]: {
    screenName: NervaControlScreenName.AiLoading,
    title: 'AI Loading',
  },
  [ScreenType.InsuranceProvider]: {
    screenName: NervaControlScreenName.InsuranceProvider,
    title: 'Insurance Provider',
  },
  [ScreenType.ProgramOverview]: {
    screenName: NervaControlScreenName.ProgramOverview,
    title: 'Program Overview',
  },
  [ScreenType.PapPublicAssistance]: {
    screenName: PapScreenName.PapPublicAssistance,
    title: 'PAP: Public Assistance',
  },
  [ScreenType.PapHouseholdIncome]: {
    screenName: PapScreenName.PapHouseholdIncome,
    title: 'PAP: Household Income',
  },
  [ScreenType.PapDiscountResult]: {
    screenName: PapScreenName.PapDiscountResult,
    title: 'PAP: Discount Result',
  },
  [ScreenType.MedicareAccessHelplines]: {
    screenName: MedicareAccessScreenName.MedicareAccessHelplinesUrgent,
    title: 'Medicare Access: Crisis',
  },
  [ScreenType.MedicareAccessMbi]: {
    screenName: MedicareAccessScreenName.MedicareAccessMBI,
    title: 'Medicare Access: MBI',
  },
  [ScreenType.ExpectationSetting]: {
    screenName: MedicareAccessScreenName.MedicareAccessExpectationSetting,
    title: 'Medicare Access: Expectation Setting',
  },
  [ScreenType.MedicareAccessCoverageLoading]: {
    screenName: MedicareAccessScreenName.MedicareAccessCoverageLoading,
    title: 'Medicare Access: Coverage Loading',
  },
  [ScreenType.MedicareAccessSafetyCheck]: {
    screenName: MedicareAccessScreenName.MedicareAccessSuicideCheck,
    title: 'Medicare Access: Safety Check',
  },
  [ScreenType.Us65PlusGrantAccess]: {
    screenName: Us65PlusScreenName.Us65PlusCoverageLoading,
    title: 'US 65 Plus: Grant Access Loading',
  },
  [ScreenType.CoverageEmployer]: {
    screenName: CoverageScreenName.CoverageUsEmployer,
    title: 'Coverage: Employer',
  },
  [ScreenType.CoverageAddress]: {
    screenName: CoverageScreenName.CoverageAddress,
    title: 'Coverage: Address',
  },
  [ScreenType.CoverageResults]: {
    screenName: CoverageScreenName.CoverageResults,
    title: 'Coverage: Results',
  },
  [ScreenType.DropdownSelect]: {
    screenName: MedicareAccessScreenName.MedicareAccessState,
    title: 'Medicare Access: State',
  },
};

const phoneScreenDemoConfig: PhoneNumberScreenConfig<SharedScreenName.PhoneNumber> = {
  screenName: SharedScreenName.PhoneNumber,
  type: ScreenType.PhoneNumber,
  layout: {
    title: 'Phone number screen',
    topBody: ['Some demo text for the phone number screen.'],
    bottomBody: ['Some demo text for the phone number screen.'],
    primaryButtonTitle: 'Next',
  },
  nextScreen: SharedScreenName.PhoneNumber,
};

const socialProofDemoConfig: SocialProofScreenConfig<MenoScreenName.EviaSocialProof> = {
  screenName: MenoScreenName.EviaSocialProof,
  type: ScreenType.SocialProof,
  layout: {
    title: 'Social Proof',
    reviewer: {
      name: 'Harry',
      image: images.pattyG,
      testimonial: ['Testimonial'],
      date: '01/02/23',
    },
    primaryButtonTitle: 'Sounds good',
  },
  nextScreen: MenoScreenName.EviaSocialProof,
};

type ScreenProps = {
  screenSize: string;
};

// Function that sets store data and returns all currently in use screens
export const Screens: FunctionComponent<ScreenProps> = ({ screenSize }) => {
  const navigation = useFlowNavigator();
  const hasIncompleteUser = useAppStore((state) => state.user.type === 'partial');
  const [isFullSize, setIsFullSize] = useState<number | null>();

  const demoScreens: Record<
    ConfigurableScreenTypes,
    { id: string; title: string; screen: ReactNode }
  > = {
    [ScreenType.Auth]: {
      id: SharedScreenName.Auth,
      title: 'Auth',
      screen: (
        <AuthScreen
          key={`demo-${SharedScreenName.Auth}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.Auth
          ) as AuthScreenConfig<SharedScreenName.Auth>)}
        />
      ),
    },
    [ScreenType.Classify]: {
      id: SharedScreenName.Classify,
      title: 'Classify',
      screen: hasIncompleteUser && (
        <ClassifyScreen
          key={`demo-${SharedScreenName.Classify}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.Classify
          ) as ClassifyScreenConfig<SharedScreenName.Classify>)}
        />
      ),
    },
    [ScreenType.DownloadApp]: {
      id: SharedScreenName.DownloadApp,
      title: 'Download App',
      screen: (
        <DownloadAppScreen
          key={`demo-${SharedScreenName.DownloadApp}`}
          navigation={navigation}
          {...(NERVA_HCP_REFERRED_PERSONALISE_CONFIG.screens.find(
            (screen) => screen.screenName === NervaControlScreenName.DownloadAppAccessCode
          ) as DownloadAppScreenConfig<SharedScreenName.DownloadApp>)}
        />
      ),
    },
    [ScreenType.PhoneNumber]: {
      id: SharedScreenName.PhoneNumber,
      title: 'Phone Number',
      screen: (
        <PhoneNumberScreen
          key={`demo-${SharedScreenName.PhoneNumber}`}
          navigation={navigation}
          {...phoneScreenDemoConfig}
        />
      ),
    },
    [ScreenType.Presentation]: {
      id: SharedScreenName.MaintenanceExperience,
      title: 'Presentation',
      screen: (
        <PresentationScreen
          key={`demo-${SharedScreenName.MaintenanceExperience}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.MaintenanceExperience
          ) as PresentationScreenConfig<SharedScreenName.MaintenanceExperience>)}
        />
      ),
    },
    [ScreenType.SingleSelect]: {
      id: SharedScreenName.Gender,
      title: 'Single Select',
      screen: (
        <SingleSelectScreen
          key={`demo-${SharedScreenName.Gender}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.Gender
          ) as SingleSelectScreenConfig<SharedScreenName.Gender>)}
        />
      ),
    },
    [ScreenType.NumberedSingleSelect]: {
      id: GAD7ScreenName.Gad7Nervous,
      title: 'Numbered Single Select',
      screen: (
        <NumberedSingleSelectScreen
          key={`demo-${GAD7ScreenName.Gad7Nervous}`}
          navigation={navigation}
          {...(MEDICARE_ACCESS_SCREENS.find(
            (screen) => screen.screenName === GAD7ScreenName.Gad7Nervous
          ) as NumberedSingleSelectScreenConfig<GAD7ScreenName.Gad7Nervous>)}
        />
      ),
    },
    [ScreenType.SingleInput]: {
      id: SharedScreenName.Age,
      title: 'Single Numeric',
      screen: (
        <SingleInputScreen
          key={`demo-${SharedScreenName.Age}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.Age
          ) as SingleInputScreenConfig<SharedScreenName.Age>)}
        />
      ),
    },
    [ScreenType.MultiSelect]: {
      id: SharedScreenName.HealthIssues,
      title: 'Multi Select',
      screen: (
        <MultiSelectScreen
          key={`demo-${SharedScreenName.HealthIssues}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.HealthIssues
          ) as MultiSelectScreenConfig<SharedScreenName.HealthIssues>)}
        />
      ),
    },
    [ScreenType.Screenshot]: {
      id: SharedScreenName.CoreProgramExperience,
      title: 'Screenshot',
      screen: (
        <ScreenshotScreen
          key={`demo-${SharedScreenName.CoreProgramExperience}`}
          navigation={navigation}
          {...(MENO_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.CoreProgramExperience
          ) as ScreenshotScreenConfig<SharedScreenName.CoreProgramExperience>)}
        />
      ),
    },

    [ScreenType.LoadingResults]: {
      id: SharedScreenName.LoadingResults,
      title: 'Loading Results',
      screen: (
        <LoadingResultsScreen
          key={`demo-${SharedScreenName.LoadingResults}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.LoadingResults
          ) as LoadingResultsScreenConfig<SharedScreenName.LoadingResults>)}
        />
      ),
    },
    [ScreenType.MoneyBackGuarantee]: {
      id: NervaControlScreenName.MoneyBackGuarantee,
      title: 'Money Back Guarantee',
      screen: (
        <MoneyBackGuaranteeScreen
          key={`demo-${NervaControlScreenName.MoneyBackGuarantee}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === NervaControlScreenName.MoneyBackGuarantee
          ) as MoneyBackGuaranteeScreenConfig<NervaControlScreenName.MoneyBackGuarantee>)}
        />
      ),
    },
    [ScreenType.Results]: {
      id: SharedScreenName.Results,
      title: 'Results',
      screen: (
        <ResultsScreen
          key={`demo-${SharedScreenName.Results}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.Results
          ) as ResultsScreenConfig<SharedScreenName.Results>)}
        />
      ),
    },
    [ScreenType.ProgramStructure]: {
      id: SharedScreenName.ProgramStructure,
      title: 'Program Structure',
      screen: (
        <ProgramStructureScreen
          key={`demo-${SharedScreenName.ProgramStructure}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.ProgramStructure
          ) as ProgramStructureScreenConfig<SharedScreenName.ProgramStructure>)}
        />
      ),
    },
    [ScreenType.ProgramBenefits]: {
      id: SharedScreenName.ProgramBenefits,
      title: 'Program Benefits',
      screen: (
        <ProgramBenefitsScreen
          key={`demo-${SharedScreenName.ProgramBenefits}`}
          navigation={navigation}
          {...(MENO_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.ProgramBenefits
          ) as ProgramBenefitsScreenConfig<SharedScreenName.ProgramBenefits>)}
        />
      ),
    },
    [ScreenType.TrialExplanation]: {
      id: SharedScreenName.TrialExplanation,
      title: 'Trial Explanation',
      screen: (
        <TrialExplanationScreen
          key={`demo-${SharedScreenName.TrialExplanation}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.TrialExplanation
          ) as TrialExplanationScreenConfig<SharedScreenName.TrialExplanation>)}
        />
      ),
    },
    [ScreenType.Payment]: {
      id: SharedScreenName.Payment,
      title: 'Payment',
      screen: (
        <PaymentScreen
          key={`demo-${SharedScreenName.Payment}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.Payment
          ) as PaymentScreenConfig<SharedScreenName.Payment>)}
        />
      ),
    },
    [ScreenType.FinishAccount]: {
      id: SharedScreenName.FinishAccount,
      title: 'Finish Account',
      screen: hasIncompleteUser && (
        <FinishAccountScreen
          key={`demo-${SharedScreenName.FinishAccount}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.FinishAccount
          ) as FinishAccountScreenConfig<SharedScreenName.FinishAccount>)}
        />
      ),
    },
    [ScreenType.UserInfoRecap]: {
      id: SharedScreenName.UserInfoRecap,
      title: 'User Info Recap',
      screen: (
        <UserInfoRecapScreen
          key={`demo-${SharedScreenName.UserInfoRecap}`}
          navigation={navigation}
          {...(MENO_CONFIG.screens.find(
            (screen) => screen.screenName === MenoScreenName.UserInfoRecap
          ) as UserInfoRecapScreenConfig<MenoScreenName.UserInfoRecap>)}
        />
      ),
    },
    [ScreenType.SocialProof]: {
      id: MenoScreenName.EviaSocialProof,
      title: 'Social Proof',
      screen: (
        <SocialProofScreen
          key={`demo-${MenoScreenName.EviaSocialProof}`}
          navigation={navigation}
          {...socialProofDemoConfig}
        />
      ),
    },
    [ScreenType.WorksBest]: {
      id: SharedScreenName.WorksBest,
      title: 'Works Best',
      screen: (
        <WorksBestScreen
          key={`demo-${SharedScreenName.WorksBest}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.WorksBest
          ) as WorksBestScreenConfig<SharedScreenName.WorksBest>)}
        />
      ),
    },
    [ScreenType.ProgramExperience]: {
      id: SharedScreenName.MaintenanceExperience,
      title: 'Program Experience',
      screen: appVariant({
        nerva: (
          <ProgramExperienceScreen
            key={`demo-${SharedScreenName.MaintenanceExperience}`}
            navigation={navigation}
            {...(QUIT_CONFIG.screens.find(
              (screen) => screen.screenName === SharedScreenName.CoreProgramExperience
            ) as ProgramExperienceScreenConfig<SharedScreenName.CoreProgramExperience>)}
          />
        ),
        meno: <></>,
        quit: (
          <ProgramExperienceScreen
            key={`demo-${SharedScreenName.MaintenanceExperience}2`}
            navigation={navigation}
            {...(QUIT_CONFIG.screens.find(
              (screen) => screen.screenName === SharedScreenName.CoreProgramExperience
            ) as ProgramExperienceScreenConfig<SharedScreenName.CoreProgramExperience>)}
          />
        ),
        pain: (
          <ProgramExperienceScreen
            key={`demo-${SharedScreenName.MaintenanceExperience}2`}
            navigation={navigation}
            {...(QUIT_CONFIG.screens.find(
              (screen) => screen.screenName === SharedScreenName.CoreProgramExperience
            ) as ProgramExperienceScreenConfig<SharedScreenName.CoreProgramExperience>)}
          />
        ),
      }),
    },
    [ScreenType.HypnotisabilityResults]: {
      id: SharedScreenName.HypnotisabilityResults,
      title: 'Hypnotisability Results',
      screen: (
        <HypnotisabilityResultsScreen
          key={`demo-${SharedScreenName.HypnotisabilityResults}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === SharedScreenName.HypnotisabilityResults
          ) as HypnotisabilityResultsScreenConfig<SharedScreenName.HypnotisabilityResults>)}
        />
      ),
    },
    [ScreenType.Referral]: {
      id: QuitScreenName.FriendReferral,
      title: 'Friend Referral',
      screen: (
        <ReferralScreen
          key={`demo-${QuitScreenName.FriendReferral}`}
          navigation={navigation}
          {...(QUIT_CONFIG.screens.find(
            (screen) => screen.screenName === QuitScreenName.FriendReferral
          ) as ReferralScreenConfig<QuitScreenName.FriendReferral>)}
        />
      ),
    },
    [ScreenType.ShareLink]: {
      id: QuitScreenName.FriendReferralSent,
      title: 'Share Link',
      screen: (
        <ShareLinkScreen
          key={`demo-${QuitScreenName.FriendReferralSent}`}
          navigation={navigation}
          {...(QUIT_CONFIG.screens.find(
            (screen) => screen.screenName === QuitScreenName.FriendReferralSent
          ) as ShareLinkScreenConfig<QuitScreenName.FriendReferralSent>)}
        />
      ),
    },
    [ScreenType.ExternalLink]: {
      id: NervaControlScreenName.HCPCallToAction,
      title: 'Book Call',
      screen: (
        <ExternalLinkScreen
          key={`demo-${NervaControlScreenName.HCPCallToAction}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === NervaControlScreenName.HCPCallToAction
          ) as ExternalLinkScreenConfig<NervaControlScreenName.HCPCallToAction>)}
        />
      ),
    },

    [ScreenType.DateOfBirth]: {
      id: SharedScreenName.DateOfBirth,
      title: 'Date of Birth',
      screen: (
        <DateOfBirthScreen
          key={`demo-${SharedScreenName.DateOfBirth}`}
          navigation={navigation}
          screenName={''}
          type={ScreenType.DateOfBirth}
          layout={{
            onPressPrimaryButton: undefined,
            primaryButtonTitle: 'Next',
            infoBox: {
              titleText: 'Why do we ask this?',
              bodyText:
                'Knowing your date of birth helps us to understand your unique ' +
                ' IBS situation and helps your healthcare provider identify you.',
            },
          }}
        />
      ),
    },

    [ScreenType.HcpReferred]: {
      id: NervaControlScreenName.WelcomeHcpReferred,
      title: 'Welcome Hcp Referred',
      screen: (
        <HcpReferredScreen
          key={`demo-${NervaControlScreenName.WelcomeHcpReferred}`}
          navigation={navigation}
          {...(NERVA_HCP_REFERRED_PERSONALISE_CONFIG.screens.find(
            (screen) => screen.screenName === NervaControlScreenName.WelcomeHcpReferred
          ) as HcpReferredScreenConfig<NervaControlScreenName.WelcomeHcpReferred>)}
        />
      ),
    },
    [ScreenType.AiLoading]: {
      id: NervaControlScreenName.AiLoading,
      title: 'AI Loading',
      screen: (
        <AiLoadingScreen
          key={`demo-${NervaControlScreenName.AiLoading}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === NervaControlScreenName.AiLoading
          ) as AiLoadingScreenConfig<NervaControlScreenName.AiLoading>)}
        />
      ),
    },
    [ScreenType.InsuranceProvider]: {
      id: NervaControlScreenName.InsuranceProvider,
      title: 'Insurance Provider',
      screen: (
        <InsuranceProviderScreen
          key={`demo-${NervaControlScreenName.InsuranceProvider}`}
          navigation={navigation}
          {...(NERVA_CONTROL_CONFIG.screens.find(
            (screen) => screen.screenName === NervaControlScreenName.InsuranceProvider
          ) as InsuranceProviderScreenConfig<NervaControlScreenName.InsuranceProvider>)}
        />
      ),
    },
    [ScreenType.ProgramOverview]: {
      id: NervaControlScreenName.ProgramOverview,
      title: 'Program Overview',
      screen: (
        <ProgramOverviewScreen
          key={`demo-${NervaControlScreenName.ProgramOverview}`}
          navigation={navigation}
          {...(NERVA_HCP_REFERRED_PERSONALISE_CONFIG.screens.find(
            (screen) => screen.screenName === NervaControlScreenName.ProgramOverview
          ) as ProgramOverviewScreenConfig<NervaControlScreenName.ProgramOverview>)}
        />
      ),
    },
    [ScreenType.PapPublicAssistance]: {
      id: PapScreenName.PapPublicAssistance,
      title: 'PAP: Public Assistance',
      screen: (
        <PapPublicAssistanceScreen
          key={`demo-${PapScreenName.PapPublicAssistance}`}
          navigation={navigation}
          screenName={PapScreenName.PapPublicAssistance}
          type={ScreenType.PapPublicAssistance}
          layout={{}}
        />
      ),
    },
    [ScreenType.PapHouseholdIncome]: {
      id: PapScreenName.PapHouseholdIncome,
      title: 'PAP: Household Income',
      screen: (
        <PapHouseholdIncomeScreen
          key={`demo-${PapScreenName.PapHouseholdIncome}`}
          navigation={navigation}
          screenName={PapScreenName.PapHouseholdIncome}
          type={ScreenType.PapHouseholdIncome}
          layout={{}}
        />
      ),
    },
    [ScreenType.PapDiscountResult]: {
      id: PapScreenName.PapDiscountResult,
      title: 'PAP: Discount Result',
      screen: (
        <PapDiscountResultScreen
          key={`demo-${PapScreenName.PapDiscountResult}`}
          navigation={navigation}
          screenName={PapScreenName.PapDiscountResult}
          type={ScreenType.PapDiscountResult}
          layout={{}}
        />
      ),
    },
    [ScreenType.MedicareAccessHelplines]: {
      id: MedicareAccessScreenName.MedicareAccessHelplinesUrgent,
      title: 'Medicare Access: Crisis',
      screen: (
        <MedicareAccessHelplinesScreen
          key={`demo-${MedicareAccessScreenName.MedicareAccessHelplinesUrgent}`}
          navigation={navigation}
          screenName={MedicareAccessScreenName.MedicareAccessHelplinesUrgent}
          type={ScreenType.MedicareAccessHelplines}
          layout={{}}
        />
      ),
    },
    [ScreenType.MedicareAccessSafetyCheck]: {
      id: MedicareAccessScreenName.MedicareAccessSuicideCheck,
      title: 'Medicare Access: Safety Check',
      screen: (
        <MedicareAccessSafetyCheckScreen
          key={`demo-${MedicareAccessScreenName.MedicareAccessSuicideCheck}`}
          navigation={navigation}
          screenName={MedicareAccessScreenName.MedicareAccessSuicideCheck}
          type={ScreenType.MedicareAccessSafetyCheck}
          layout={{
            body: [
              'You reported thoughts of being better off dead or of hurting yourself. Do you have thoughts of suicide?',
            ],
            choices: ['Yes', 'No'],
          }}
        />
      ),
    },
    [ScreenType.MedicareAccessMbi]: {
      id: MedicareAccessScreenName.MedicareAccessMBI,
      title: 'Medicare Access: MBI',
      screen: (
        <MedicareAccessMbiScreen
          key={`demo-${MedicareAccessScreenName.MedicareAccessMBI}`}
          navigation={navigation}
          screenName={MedicareAccessScreenName.MedicareAccessMBI}
          type={ScreenType.MedicareAccessMbi}
          layout={{
            title: "First, let's confirm a few details",
            subtitle: "We'll use this information to confirm your Medicare eligibility.",
            medicareSectionTitle: 'Medicare details',
            personalSectionTitle: 'Personal details',
            mbiLabel: 'Medicare Beneficiary Identifier',
            mbiPlaceholder: 'Enter your 11-character MBI',
            mbiHelperText: '11 characters from your Medicare card (e.g. 1EG4-TE5-MK72).',
            givenNameLabel: 'Legal given name (including middle name)',
            familyNameLabel: 'Legal family name',
            addressLabel: 'Residential address',
            addressHelperText: 'Include street number and name, suburb, state and zip code.',
            primaryButtonTitle: 'Next',
          }}
        />
      ),
    },
    [ScreenType.Us65PlusGrantAccess]: {
      id: Us65PlusScreenName.Us65PlusCoverageLoading,
      title: 'US 65 Plus: Grant Access Loading',
      screen: (
        <Us65PlusGrantAccessScreen
          key={`demo-${Us65PlusScreenName.Us65PlusCoverageLoading}`}
          navigation={navigation}
          screenName={Us65PlusScreenName.Us65PlusCoverageLoading}
          type={ScreenType.Us65PlusGrantAccess}
        />
      ),
    },
    [ScreenType.MedicareAccessCoverageLoading]: {
      id: MedicareAccessScreenName.MedicareAccessCoverageLoading,
      title: 'Medicare Access: Coverage Loading',
      screen: (
        <MedicareAccessCoverageLoadingScreen
          key={`demo-${MedicareAccessScreenName.MedicareAccessCoverageLoading}`}
          navigation={navigation}
          screenName={MedicareAccessScreenName.MedicareAccessCoverageLoading}
          type={ScreenType.MedicareAccessCoverageLoading}
          layout={{
            title: 'Checking your coverage',
            subtitle: 'This will only take a moment.',
            showAiBadge: false,
            loadingBars: [
              { title: 'Saving your responses', startNumber: 0, endNumber: 100, durationMs: 2500 },
              {
                title: 'Checking your Medicare coverage',
                startNumber: 0,
                endNumber: 100,
                durationMs: 3000,
              },
              {
                title: 'Confirming your eligibility',
                startNumber: 0,
                endNumber: 100,
                durationMs: 3000,
              },
            ],
            footnoteHeader: 'Confirming eligibility',
          }}
          rejectedScreen={MedicareAccessScreenName.MedicareAccessControl}
          disqualifiedScreen={MedicareAccessScreenName.MedicareAccessInsurerNotCovered}
        />
      ),
    },
    [ScreenType.CoverageEmployer]: {
      id: CoverageScreenName.CoverageUsEmployer,
      title: 'Coverage: Employer',
      screen: (
        <CoverageEmployerScreen
          key={`demo-${CoverageScreenName.CoverageUsEmployer}`}
          navigation={navigation}
          {...(COVERAGE_SCREENS.find(
            (screen) => screen.screenName === CoverageScreenName.CoverageUsEmployer
          ) as CoverageEmployerScreenConfig<CoverageScreenName>)}
        />
      ),
    },
    [ScreenType.CoverageAddress]: {
      id: CoverageScreenName.CoverageAddress,
      title: 'Coverage: Address',
      screen: (
        <CoverageAddressScreen
          key={`demo-${CoverageScreenName.CoverageAddress}`}
          navigation={navigation}
          {...(COVERAGE_SCREENS.find(
            (screen) => screen.screenName === CoverageScreenName.CoverageAddress
          ) as CoverageAddressScreenConfig<CoverageScreenName>)}
        />
      ),
    },
    [ScreenType.CoverageResults]: {
      id: CoverageScreenName.CoverageResults,
      title: 'Coverage: Results',
      screen: (
        <CoverageResultsScreen
          key={`demo-${CoverageScreenName.CoverageResults}`}
          navigation={navigation}
          {...({
            screenName: CoverageScreenName.CoverageResults,
            type: ScreenType.CoverageResults,
            layout: COVERAGE_RESULTS_LAYOUT,
            assistanceScreen: PapScreenName.PapPublicAssistance,
          } as CoverageResultsScreenConfig<CoverageScreenName | PapScreenName>)}
        />
      ),
    },
    [ScreenType.ExpectationSetting]: {
      id: MedicareAccessScreenName.MedicareAccessExpectationSetting,
      title: 'Medicare Access: Expectation Setting',
      screen: (
        <ExpectationSettingScreen
          key={`demo-${MedicareAccessScreenName.MedicareAccessExpectationSetting}`}
          navigation={navigation}
          screenName={MedicareAccessScreenName.MedicareAccessExpectationSetting}
          type={ScreenType.ExpectationSetting}
          layout={{
            title: 'What to expect',
            intro:
              "Nerva works best as a partnership – we bring the program and the science, and we're alongside you the whole way.",
            promise: {
              heading: 'What we promise you:',
              items: [
                'Guide you through every step of the 6-week program.',
                'Backed by real science from Monash University.',
                'Keep your care team in the loop.',
              ],
            },
            ask: {
              heading: "What we'll ask of you:",
              items: [
                'A few minutes a day for your guided session.',
                'Your check-ins along the way (start, 6 weeks, then every 3 months).',
              ],
            },
            closing:
              "We've seen this work for thousands of people. We'd love to help it work for you, too.",
            namePrompt: 'Type your name to begin:',
            namePlaceholder: 'Your name',
            primaryButtonTitle: "I'm in — start my program",
          }}
        />
      ),
    },
    [ScreenType.DropdownSelect]: {
      id: MedicareAccessScreenName.MedicareAccessState,
      title: 'Medicare Access: State',
      screen: (
        <DropdownSelectScreen
          key={`demo-${MedicareAccessScreenName.MedicareAccessState}`}
          navigation={navigation}
          screenName={MedicareAccessScreenName.MedicareAccessState}
          type={ScreenType.DropdownSelect}
          layout={MEDICARE_ACCESS_STATE_LAYOUT}
        />
      ),
    },
  };

  return (
    <>
      {Object.values(demoScreens)
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((screenConfig, index) => {
          const Screen = screenConfig.screen;

          return (
            <DemoScreensWrapper
              key={screenConfig.title}
              id={screenConfig.id}
              isSmallSize={screenSize === 'small'}
              isMediumSize={screenSize === 'medium'}
              isLargeSize={screenSize === 'large'}
              isFullSize={isFullSize === index}
            >
              <DemoScreensHeader
                onClick={() => (isFullSize === index ? setIsFullSize(null) : setIsFullSize(index))}
              >
                {screenConfig.title}
                <span>🔎</span>
              </DemoScreensHeader>
              <DemoScreensComponent>{Screen}</DemoScreensComponent>
            </DemoScreensWrapper>
          );
        })}
    </>
  );
};
